import { useTranslation } from 'react-i18next'
import {
  type ChatToolContext,
  pickActions,
  type ToolActionHandlers,
  type ToolActionKind,
} from '@/components/agent/tools/_shared/chat-tool-actions'
import { useChatToolContext } from '@/components/agent/tools/_shared/useChatToolContext'

/**
 * Resolved view-model for an agent tool-result action footer. Pure data —
 * the presentational `ToolCardActions` / `MapCardActions` components render
 * this without touching the chat context, the store, or i18n decisions.
 */
export interface ToolCardActionsModel {
  /** Primary CTA to render. */
  primary: ToolActionKind
  /** Remaining actions in priority order; footers render at most the first. */
  secondary: ToolActionKind[]
  /** Action-aware micro-copy, already localised. Empty when not applicable. */
  hint: string
}

type TFn = (key: string, opts?: Record<string, unknown>) => string

/**
 * Resolve which actions a tool-result footer should offer, from the live
 * chat-tool context. Returns null when there's nothing to show (no case in
 * scope, or the tool exposes no executable handlers) — callers render the
 * footer only when this is non-null. This is the logic half of the footer;
 * keep it out of the presentational components.
 */
export function useToolCardActions(
  entityIds: readonly string[] | undefined,
  handlers: ToolActionHandlers,
  opts?: { hideHint?: boolean }
): ToolCardActionsModel | null {
  const { t } = useTranslation()
  const ctx = useChatToolContext()

  const ids = entityIds ?? []
  const available = new Set((Object.keys(handlers) as ToolActionKind[]).filter(k => handlers[k]))
  if (available.size === 0 || !ctx) return null

  const { primary, secondary } = pickActions(ids, available, ctx)
  if (!primary) return null

  return {
    primary,
    secondary,
    hint: opts?.hideHint ? '' : buildHint(t, primary, ids, ctx),
  }
}

/**
 * Compute just the action-aware hint string without resolving the full
 * footer — for callers that position the hint themselves (e.g. FindingCard
 * renders it leftmost and passes `hideHint` to ToolCardActions).
 */
export function useToolCardHint(
  entityIds: readonly string[] | undefined,
  handlers: ToolActionHandlers
): string {
  const { t } = useTranslation()
  const ctx = useChatToolContext()
  if (!ctx) return ''
  const ids = entityIds ?? []
  const available = new Set((Object.keys(handlers) as ToolActionKind[]).filter(k => handlers[k]))
  if (available.size === 0) return ''
  const { primary } = pickActions(ids, available, ctx)
  if (!primary) return ''
  return buildHint(t, primary, ids, ctx)
}

function buildHint(
  t: TFn,
  primary: ToolActionKind,
  entityIds: readonly string[],
  ctx: Pick<ChatToolContext, 'hasActiveView' | 'loadedEntityIds'>
): string {
  // Empty-state branch (Show on graph) doesn't need a hint — the button
  // label already says it all and there's nothing to compare against.
  if (!ctx.hasActiveView || entityIds.length === 0) return ''

  const total = entityIds.length
  const loaded = entityIds.reduce((n, id) => (ctx.loadedEntityIds.has(id) ? n + 1 : n), 0)

  if (loaded === total) return t('chat.toolActions.hint.allLoaded', { count: total })
  if (loaded === 0) return t('chat.toolActions.hint.noneLoaded', { count: total })
  // Force the plural pickers to use the `total` form so "12 of 24" picks the
  // right case regardless of which number happens to be 1.
  return t('chat.toolActions.hint.partial', { count: total, loaded, total })
}
